/*
 * AI TRANSLATION INTO THE READER'S PORTAL LANGUAGE — moved out of 61_AiHub.jsx on 2026-10-28 so GCC can use the same hook
 * (Max: "chuyển ngữ trực tiếp theo system language … các câu trả lời của catchy hay ai đó cũng chuyển về theo đúng ngôn ngữ
 * của hệ thống"). The server half is AI Hub's /api/ai-hub/translate (cached per text and language in 00_PORTAL_ai_cache).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AiTag from './AiTag';
import { BACKEND_URL } from './backendConfig';

const callTranslate = async (body) => {
  const r = await fetch(`${BACKEND_URL}/api/ai-hub/translate`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
};

/* what people typed, shown in the reader's portal language (Jay, 27/10: "Please translate by AI in Portal system
   language"). The server translates once per text and language and keeps it; a text that is plainly in the reader's
   language already is not even sent. A translated cell carries the AI mark, and "Show original" puts the words back
   as they were typed. */
const HANGUL = /[\uac00-\ud7a3]/;
const HAN = /[\u4e00-\u9fff]/;
const VIET = /[ăâđêôơưạảấầẩẫậắằẳẵặẹẻẽếềểễệỉịọỏốồổỗộớờởỡợụủứừửữựỳỵỷỹ]/i;
/* Vietnamese typed without its marks ("FM la gi?", "labor tinh nhu the nao?") is plain ASCII and passed for English
   (Max, 27/10): two or more of these everyday words and it is sent for translation like any other */
const VIET_BARE = /\b(khong|nhu|nao|gi|duoc|cua|thi|sao|tinh|phai|moi|nay|voi|nhung|toi|la|ko|vay|roi|dung|cung|chua)\b/gi;
export const plainlyIn = (lang, x) => {
  if (lang === 'ko') return HANGUL.test(x);
  if (lang === 'vi') return VIET.test(x);
  if (lang === 'zh') return HAN.test(x) && !HANGUL.test(x);
  return !HANGUL.test(x) && !HAN.test(x) && !VIET.test(x) && new Set((x.match(VIET_BARE) || []).map((w) => w.toLowerCase())).size < 2;
};
export function useAiTranslate(texts) {
  const { i18n } = useTranslation();
  const lang = ['ko', 'vi', 'zh'].includes(String(i18n.language || '').slice(0, 2)) ? String(i18n.language).slice(0, 2) : 'en';
  const [done, setDone] = useState({});
  const [err, setErr] = useState('');
  const asked = useRef(new Set());
  const key = texts.join('\u0001');
  useEffect(() => {
    const want = [...new Set(texts.filter((x) => x && x.trim() && !plainlyIn(lang, x) && !asked.current.has(`${lang}|${x}`)))];
    if (!want.length) return;
    want.forEach((x) => asked.current.add(`${lang}|${x}`));
    /* short texts first and several requests at once (Max, 27/10: "tốc độ chuyển ngữ bị chậm"): the questions show
       translated in a moment, each long answer as soon as its own request is back; a list seen before is cached */
    want.sort((x, y) => x.length - y.length);
    const parts = [];
    for (let i = 0; i < want.length;) {
      const part = []; let size = 0;
      while (i < want.length && part.length < 8 && (!part.length || size + want[i].length <= 2500)) { size += want[i].length; part.push(want[i]); i += 1; }
      parts.push(part);
    }
    let next = 0;
    const worker = async () => {
      while (next < parts.length) {
        const part = parts[next]; next += 1;
        try {
          const r = await callTranslate({ lang, texts: part });
          setDone((d) => { const n = { ...d }; part.forEach((x, k) => { n[`${lang}|${x}`] = r.out[k] || null; }); return n; });
        } catch (e) { part.forEach((x) => asked.current.delete(`${lang}|${x}`)); setErr(e.message); }
      }
    };
    for (let k = 0; k < Math.min(6, parts.length); k += 1) worker();
  }, [key, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  return { tr: (x) => (x ? done[`${lang}|${x}`] || null : null), err };
}
export function Tr({ text, tr, orig }) {
  const { t } = useTranslation();
  const v = !orig && tr(text);
  if (!v) return text || null;
  return <span className="ah-tr" title={t('xAiHub.trMark')}><AiTag size={11} className="ah-trmark" />{v}</span>;
}
